import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import axios from 'axios';
import { CheckCircle, Calendar, User, CreditCard, Building2, Clock, Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import InactivityTimer from '../../components/InactivityTimer';

const OpdVisitSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [visitData, setVisitData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [consultationDownloadUrl, setConsultationDownloadUrl] = useState(null);

    const visitNo = searchParams.get('visitNo');

    useEffect(() => {
        const fetchVisitData = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get(`/api/opd-visit/visit/${visitNo}`);
                
                if (response.data.success) {
                    setVisitData(response.data.data);
                    
                    // After successfully fetching visit data, create consultation in HIMS
                    await createConsultationInHIMS(response.data.data);
                } else {
                    setError('Failed to fetch visit details');
                }
            } catch (err) {
                console.error('Error fetching visit data:', err);
                setError('Error loading visit details');
            } finally {
                setLoading(false);
            }
        };

        if (visitNo) {
            fetchVisitData();
        } else {
            setError('No visit number provided');
            setLoading(false);
        }
    }, [visitNo]);

    const createConsultationInHIMS = async (visitData) => {
        try {
            // Check if consultation already created for this visit
            const consultationKey = `consultation_created_${visitNo}`;
            const alreadyCreated = localStorage.getItem(consultationKey);
            
            if (alreadyCreated) {
                console.log('Consultation already created for this visit');
                // If already created, try to load cached download URL
                try {
                    const cachedData = JSON.parse(alreadyCreated);
                    if (cachedData.downloadUrl) {
                        let downloadUrl = cachedData.downloadUrl;
                        // Decode the URL if it contains escaped characters
                        downloadUrl = downloadUrl.replace(/\\u003d/g, '=').replace(/\\u0026/g, '&');
                        console.log('Loading cached download URL:', downloadUrl);
                        setConsultationDownloadUrl(downloadUrl);
                    }
                } catch (e) {
                    console.warn('Could not parse cached consultation data:', e);
                }
                return;
            }

            // Parse appointment metadata to get additional details
            let appointmentMetadata = {};
            try {
                appointmentMetadata = JSON.parse(visitData.payment.appointmentMetadata || '{}');
            } catch (e) {
                console.warn('Could not parse appointment metadata:', e);
            }

            const consultationPayload = {
                createdBy: "2",
                departmentId: visitData.visit.departmentId?.toString() || "1183500",
                consultantId: visitData.visit.doctorId?.toString() || "4325",
                patientId: visitData.patient.externalPatientId?.toString() || "3870366",
                siteId: visitData.visit.hospitalId?.toString() || "2468",
                serviceCenterId: "86158",
                unitId: visitData.visit.unitId || "271",
                payment: {
                    paymentId: visitData.payment.razorpayPaymentId || "Razorpay",
                    amount: visitData.payment.amount_received / 100, // Convert paise to rupees
                    currency: visitData.payment.currency || "INR",
                    orderId: visitData.payment.razorpayOrderId || "Razorpay",
                    status: visitData.payment.status?.toUpperCase() === 'COMPLETED' ? 'SUCCESS' : visitData.payment.status?.toUpperCase()
                }
            };

            console.log('Creating consultation in HIMS with payload:', consultationPayload);

            const himsResponse = await axios.post(
                `${process.env.REACT_APP_KIOSK_OPS_URL}/consultation/create`,
                consultationPayload,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1') || ''}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log('HIMS consultation response:', himsResponse.data);
            console.log('Download URL from response:', himsResponse.data?.downloadUrl);
            
            // Extract download URL from response
            if (himsResponse.data && himsResponse.data.downloadUrl) {
                // Decode the URL if it contains escaped characters
                let downloadUrl = himsResponse.data.downloadUrl;
                try {
                    // Replace Unicode escape sequences with actual characters
                    downloadUrl = downloadUrl.replace(/\\u003d/g, '=').replace(/\\u0026/g, '&');
                    console.log('Decoded URL:', downloadUrl);
                } catch (e) {
                    console.warn('Could not decode URL:', e);
                }
                
                console.log('Setting consultation download URL:', downloadUrl);
                setConsultationDownloadUrl(downloadUrl);
                
                // Cache the response including download URL
                localStorage.setItem(consultationKey, JSON.stringify({
                    created: true,
                    downloadUrl: downloadUrl,
                    encounterId: himsResponse.data.message
                }));
            } else {
                console.warn('No download URL in response:', himsResponse.data);
                // Mark as created even without download URL
                localStorage.setItem(consultationKey, JSON.stringify({ created: true }));
            }
            
        } catch (error) {
            console.error('Error creating consultation in HIMS:', error.response?.data || error.message);
            console.error('Full error:', error);
            // Don't throw error to avoid blocking the success page display
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch {
            return dateString;
        }
    };

    const handleBackToDashboard = () => {
        navigate('/dashboard', { replace: true });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#0FA968] mx-auto mb-4"></div>
                    <p className="text-xl text-gray-600">Loading visit details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-4">
                        <p className="font-semibold text-xl mb-2">Error</p>
                        <p>{error}</p>
                    </div>
                    <button
                        onClick={handleBackToDashboard}
                        className="px-6 py-3 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200"
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <InactivityTimer />
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="container mx-auto px-4 max-w-6xl">
                    {/* Success Header */}
                    <div className="bg-white rounded-xl shadow-lg p-8 mb-6 text-center">
                    <div className="flex justify-center mb-4">
                        <CheckCircle className="w-20 h-20 text-green-500" />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">
                        OPD Visit Booked Successfully!
                    </h1>
                    <p className="text-gray-600 text-lg">
                        Your consultation has been confirmed and payment completed.
                    </p>
                    <div className="mt-4 inline-block bg-green-100 text-green-800 px-6 py-2 rounded-full">
                        <span className="font-semibold">Visit No: </span>
                        <span className="font-bold text-xl">{visitData?.visit?.visitNo}</span>
                    </div>
                </div>

                {/* Visit Details */}
                {visitData && (
                    <>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                            {/* Patient Information */}
                            <div className="bg-white rounded-xl shadow-lg p-6">
                                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                                    <User className="w-6 h-6 mr-2 text-[#0FA968]" />
                                    Patient Information
                                </h2>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-sm text-gray-600">Patient Name</p>
                                        <p className="text-lg font-semibold text-gray-800">
                                            {visitData.patient?.prefix} {visitData.patient?.firstName} {visitData.patient?.middleName} {visitData.patient?.lastName}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">MRN</p>
                                        <p className="text-lg font-semibold text-gray-800">
                                            {visitData.patient?.mrno}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Mobile Number</p>
                                        <p className="text-lg font-semibold text-gray-800">
                                            {visitData.patient?.contactNumber}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Email</p>
                                        <p className="text-lg font-semibold text-gray-800">
                                            {visitData.patient?.emailAddress || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Visit Details */}
                            <div className="bg-white rounded-xl shadow-lg p-6">
                                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                                    <Calendar className="w-6 h-6 mr-2 text-[#0FA968]" />
                                    Visit Details
                                </h2>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-sm text-gray-600">Department</p>
                                        <p className="text-lg font-semibold text-gray-800">
                                            {visitData.visit?.department}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Doctor Name</p>
                                        <p className="text-lg font-semibold text-gray-800">
                                            {visitData.visit?.doctorName}
                                        </p>
                                    </div>
                                    {visitData.visit?.unitId && (
                                        <div>
                                            <p className="text-sm text-gray-600">Unit ID</p>
                                            <p className="text-lg font-semibold text-gray-800">
                                                {visitData.visit?.unitId}
                                            </p>
                                        </div>
                                    )}
                                    <div>
                                        <p className="text-sm text-gray-600">Visit Date</p>
                                        <p className="text-lg font-semibold text-gray-800">
                                            {formatDate(visitData.visit?.visitDate)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Booked On</p>
                                        <p className="text-lg font-semibold text-gray-800">
                                            {formatDate(visitData.visit?.createdAt)} at {formatTime(visitData.visit?.createdAt)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Payment Details */}
                        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                                <CreditCard className="w-6 h-6 mr-2 text-[#0FA968]" />
                                Payment Details
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div>
                                    <p className="text-sm text-gray-600">Consultation Fee</p>
                                    <p className="text-2xl font-bold text-[#0FA968]">
                                        ₹{visitData.visit?.consultantFee}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Payment Status</p>
                                    <span className="inline-block px-4 py-2 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                                        {visitData.payment?.status?.toUpperCase()}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Payment ID</p>
                                    <p className="text-sm font-semibold text-gray-800 break-all">
                                        {visitData.payment?.razorpayPaymentId}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Order ID</p>
                                    <p className="text-sm font-semibold text-gray-800 break-all">
                                        {visitData.payment?.razorpayOrderId}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Important Instructions */}
                        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6 mb-6">
                            <h3 className="text-lg font-bold text-blue-800 mb-3 flex items-center">
                                <Clock className="w-5 h-5 mr-2" />
                                Important Instructions
                            </h3>
                            <ul className="list-disc list-inside space-y-2 text-blue-900">
                                <li>Please arrive 15 minutes before your consultation time</li>
                                <li>Carry a valid ID proof and this visit number</li>
                                <li>Keep your Visit Number ({visitData?.visit?.visitNo}) handy</li>
                                <li>For any queries, contact the hospital reception</li>
                            </ul>
                        </div>

                        {/* Debug Info - Remove after testing */}
                        {/* <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-6">
                            <p className="text-sm font-semibold text-yellow-800 mb-2">Debug Info:</p>
                            <p className="text-xs text-yellow-700">
                                Download URL State: {consultationDownloadUrl ? 'SET' : 'NOT SET'}
                            </p>
                            {consultationDownloadUrl && (
                                <p className="text-xs text-yellow-700 break-all mt-1">
                                    URL: {consultationDownloadUrl}
                                </p>
                            )}
                        </div> */}

                        {/* Case Sheet Download Section */}
                        {consultationDownloadUrl ? (
                            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                                    Download Case Sheet
                                </h3>
                                <div className="flex flex-col items-center">
                                    {/* QR Code */}
                                    <div className="bg-gray-50 p-6 rounded-lg mb-4 inline-block border-2 border-gray-200">
                                        <QRCodeSVG
                                            value={consultationDownloadUrl}
                                            size={200}
                                            level="H"
                                            includeMargin={true}
                                        />
                                    </div>
                                    <p className="text-sm text-gray-600 mb-4 text-center">
                                        Scan this QR code or click the button below to download your case sheet
                                    </p>
                                    {/* Download Button */}
                                    <a
                                        href={consultationDownloadUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow hover:bg-blue-700 transition duration-200 text-lg"
                                    >
                                        <Download className="w-5 h-5" />
                                        Download Case Sheet (PDF)
                                    </a>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 mb-6 text-center">
                                <p className="text-sm text-gray-600">
                                    Generating case sheet... Please wait.
                                </p>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex justify-center gap-4">
                            <button
                                onClick={handleBackToDashboard}
                                className="px-8 py-3 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200 text-lg"
                            >
                                Back to Dashboard
                            </button>
                            {/* <button
                                onClick={() => window.print()}
                                className="px-8 py-3 bg-white border-2 border-[#0FA968] text-[#0FA968] font-semibold rounded-lg shadow hover:bg-[#EFFEF7] transition duration-200 text-lg"
                            >
                                Print Receipt
                            </button> */}
                        </div>
                    </>
                )}
            </div>
        </div>
        </>
    );
};

export default OpdVisitSuccess;
